import { TooltipWrapper } from 'modules/experiments/components/ui';
import { STATES_WITH_DELETE_ACTION } from 'modules/experiments/constants';
import { useModalContext } from 'modules/experiments/contexts';
import {
  getCampaignMessageMedium,
  getCampaignType,
  getExperimentCampaignData,
  getExperimentData,
  getExperimentId,
  getExperimentName,
  getExperimentStateStatus,
} from 'modules/experiments/ducks/selectors';
import {
  useDeleteExperiment,
  useEndExperiment,
  useExperimentsTranslations,
  useExperimentsUserPermission,
} from 'modules/experiments/hooks';
import { useExperimentTakeover } from 'modules/experiments/takeover';
import { ExperimentTakeoverSource } from 'modules/experiments/takeover/constants';
import { BasePageExperimentData, ExperimentNavigateOptions } from 'modules/experiments/types';
import { getExperimentStatesWithEditAction, isRunningExperiment } from 'modules/experiments/utils';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';
import { BUTTON_VARIANTS, SecondaryDestructiveButton } from 'ui/button';
import { Edit16 } from 'ui/icons';
import { LinkButton } from 'ui/linkButton';
import { MODAL_VARIANTS } from 'ui/modal';
import { getExperimentsIndexPageLink } from 'utils/appRoutes';
import { MonitorPageActionWrapper, MonitorPageActionsWrapper, StyledEditIcon } from './Styled';

const getPageActions = () => {
  const { t } = useExperimentsTranslations();
  const campaignMessageMedium = useSelector(getCampaignMessageMedium);
  const { nonManageAndLaunchPermission, viewOnlyPermission } =
    useExperimentsUserPermission(campaignMessageMedium);
  const { getExperimentLinkAttributes } = useExperimentTakeover();
  const { showModal, hideModal } = useModalContext();
  const navigate = useNavigate();
  const experiment = useSelector(getExperimentData) as BasePageExperimentData;
  const campaign = useSelector(getExperimentCampaignData) as BasePageExperimentData['campaign'];
  const experimentId = useSelector(getExperimentId);
  const experimentName = useSelector(getExperimentName);

  // Mutation hooks
  const endExperiment = useEndExperiment({
    id: experimentId,
    onComplete() {
      hideModal();
    },
  });
  const deleteExperiment = useDeleteExperiment({
    id: experimentId,
    name: experimentName,
    showSuccessToaster: false,
    onError() {
      hideModal();
    },
    onSuccess() {
      hideModal();
      navigate(getExperimentsIndexPageLink(), {
        state: { experimentName },
      } as ExperimentNavigateOptions);
    },
  });

  // Determine which actions to show
  const state = useSelector(getExperimentStateStatus);
  const campaignType = useSelector(getCampaignType);
  const editable = getExperimentStatesWithEditAction(campaignType).includes(state);
  const endable = isRunningExperiment(campaignType, state);
  const deletable = STATES_WITH_DELETE_ACTION.includes(state);

  // Edit experiment takeover trigger
  const { href = '#' } = getExperimentLinkAttributes({
    source: ExperimentTakeoverSource.EXPERIMENT_ANALYTICS,
    experiment,
    campaign,
  });

  return [
    {
      id: 'deleteExperiment',
      tooltip: nonManageAndLaunchPermission ? t('permissions.deleteExperiment') : null,
      component: (
        <SecondaryDestructiveButton
          data-test="experiment-delete-button"
          isDisabled={nonManageAndLaunchPermission}
          onClick={() =>
            showModal({
              variant: MODAL_VARIANTS.DELETE,
              id: 'delete-experiment',
              title: t('monitor.deleteModal.header'),
              body: t('monitor.deleteModal.body'),
              buttonText: t('monitor.deleteModal.button'),
              inProgressButtonText: t('monitor.deleteModal.progress'),
              onConfirm: () => {
                deleteExperiment();
              },
            })
          }
        >
          {t('monitor.header.deleteExperiment')}
        </SecondaryDestructiveButton>
      ),
      enabled: deletable,
    },
    {
      id: 'edit',
      tooltip: viewOnlyPermission ? t('permissions.editExperiment') : null,
      component: (
        <LinkButton
          data-test="experiment-edit-button"
          variant={BUTTON_VARIANTS.SECONDARY}
          to={!viewOnlyPermission ? href : '#'}
        >
          <StyledEditIcon>
            <Edit16 />
          </StyledEditIcon>
          {t('monitor.header.edit')}
        </LinkButton>
      ),
      enabled: editable,
    },
    {
      id: 'endExperiment',
      tooltip: nonManageAndLaunchPermission ? t('permissions.endExperiment') : null,
      component: (
        <SecondaryDestructiveButton
          data-test="experiment-end-button"
          isDisabled={nonManageAndLaunchPermission}
          onClick={() =>
            showModal({
              variant: MODAL_VARIANTS.DELETE,
              id: 'end-experiment',
              title: t('monitor.endModal.header'),
              body: t('monitor.endModal.body'),
              buttonText: t('monitor.endModal.button'),
              inProgressButtonText: t('monitor.endModal.progress'),
              onConfirm: () => {
                endExperiment();
              },
            })
          }
        >
          {t('monitor.header.endExperiment')}
        </SecondaryDestructiveButton>
      ),
      enabled: endable,
    },
  ];
};

export const MonitorPageActions = () => {
  const actions = getPageActions();

  return (
    <>
      <MonitorPageActionsWrapper>
        {actions
          .filter((action) => action.enabled)
          .map((action, i) => (
            <TooltipWrapper key={i} tooltipContent={action.tooltip}>
              <MonitorPageActionWrapper>{action.component}</MonitorPageActionWrapper>
            </TooltipWrapper>
          ))}
      </MonitorPageActionsWrapper>
    </>
  );
};
